/**
 * Behavioural event names the investor portal and admin app may emit via the
 * typed `track()` helpers (web-app `lib/posthog.ts`, admin-app `lib/posthog.ts`).
 *
 * Single source of truth for two things:
 * 1. the `PostHogEventName` union — gives call sites autocomplete and turns typos
 *    into compile errors;
 * 2. the runtime allowlist — `ALLOWED_EVENTS` in `posthogAllowlist.ts` spreads this
 *    list, so any name added here is automatically permitted by the `before_send`
 *    PII gate and the two cannot drift.
 *
 * Names are `snake_case`, shaped `domain_object_action`, past tense. Add new events
 * here as each funnel is instrumented; add their property keys to
 * `ALLOWED_APP_PROPERTIES`. Catalogue: §5 of the tracking brief.
 */
export const BEHAVIOURAL_EVENTS = [
	// Account creation & sign-in (brief §5.1)
	"signup_started",
	"signup_email_submitted",
	"email_verified",
	"password_set",
	"account_created",
	"login_completed",
	"login_failed",
	// Identity verification — Onfido KYC (brief §5.2)
	"kyc_started",
	"kyc_submitted",
	"kyc_finished",
	"kyc_cancelled",
	"kyc_error",
	// Investing entity setup (brief §5.3)
	"entity_setup_started",
	"entity_setup_step_completed",
	"entity_created",
	// Subscription — single + batch (brief §5.4)
	"subscription_started",
	"subscription_step_viewed",
	"subscription_step_completed",
	"subscription_amount_entered",
	"subscription_submitted",
	"subscription_abandoned",
	"batch_subscription_started",
	"batch_subscription_submitted",
	// Bank account entry & verification (brief §5.5)
	"bank_account_add_started",
	"bank_account_added",
	"bank_account_verification_started",
	"bank_account_verification_submitted",
	// Notifications — compliance & action tasks (brief §5.6)
	"notification_shown",
	"notification_opened",
	"notification_completed",
	"notification_skipped",
	// Portfolio & engagement (brief §5.7)
	// NB: brief's `performance_chart_interacted` is intentionally omitted — the
	// portfolio/holding charts are read-only (no metric/range picker), so there is
	// no interaction to capture. Revisit if the charts gain controls.
	"portfolio_viewed",
	"holding_detail_viewed",
	"transactions_viewed",
	"document_viewed",
	"document_downloaded",
	"distribution_viewed",
	"entity_switched",
	// --- admin-app behavioural events (APP-24321) ---
	// Capital calls
	"capital_call_batch_created",
	// Distributions (duplicated fires instead of created when creating from a duplicate)
	"distribution_created",
	"distribution_duplicated",
	"distribution_published",
	// Funds
	"fund_created",
	// Accounts / investing entities (admin-created; the portal's own flow uses entity_created)
	"investing_entity_created",
	// Bulk imports & operations
	"bulk_import_started",
	"bulk_import_submitted",
	"bulk_operation_completed",
	"bulk_operation_failed",
	// Allocations
	"allocation_created",
	"allocation_csv_exported",
	// Identity verification (frontend-observable actions only — see D7 on APP-24321)
	"identity_verification_requested",
	"identity_manually_verified",
	"address_verified",
	"pep_result_accepted",
	"pep_result_rejected",
	// Data room
	"data_room_document_uploaded",
	"data_room_document_deleted",
	"data_room_published",
	// Reports & statements (report_generated covers flows where download success
	// is not frontend-observable: async operations and streaming-proxy redirects)
	"report_downloaded",
	"report_generated",
	"statement_batch_created",
	// Monthly Automations report
	"automations_report_opened",
	"automations_report_section_reached",
	"automations_report_watched_again",
	"automations_report_contact_clicked",
	"automations_report_pdf_downloaded",
] as const;

export type PostHogEventName = (typeof BEHAVIOURAL_EVENTS)[number];
